// Sign in with Apple (web) — loads Apple's hosted JS SDK once and inits it.
// The backend (/api/auth/apple/verify-token) verifies the returned identity
// token against Apple's JWKS + allowed audiences, so the browser only needs
// to obtain the id_token via the popup flow.
const APPLE_SDK_URL =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

export const APPLE_SERVICES_ID = process.env.REACT_APP_APPLE_SERVICES_ID || 'com.soulsanskar.academy.signin';
export const APPLE_REDIRECT_URI =
  process.env.REACT_APP_APPLE_REDIRECT_URI ||
  (typeof window !== 'undefined' ? window.location.origin : '');

let sdkPromise;

export function loadAppleSDK() {
  if (typeof window !== 'undefined' && window.AppleID) return Promise.resolve(window.AppleID);
  if (sdkPromise) return sdkPromise;

  sdkPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${APPLE_SDK_URL}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.AppleID));
      existing.addEventListener('error', () => reject(new Error('Unable to load Apple JS SDK')));
      return;
    }
    const script = document.createElement('script');
    script.src = APPLE_SDK_URL;
    script.async = true;
    script.onload = () => (window.AppleID ? resolve(window.AppleID) : reject(new Error('AppleID unavailable')));
    script.onerror = () => reject(new Error('Unable to load Apple JS SDK'));
    document.head.appendChild(script);
  });
  return sdkPromise;
}

function randomState() {
  const bytes = new Uint8Array(24);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

// Opens the Apple popup and resolves with the identity token + optional name.
export async function appleSignInPopup() {
  const AppleID = await loadAppleSDK();
  AppleID.auth.init({
    clientId: APPLE_SERVICES_ID,
    scope: 'name email',
    redirectURI: APPLE_REDIRECT_URI,
    usePopup: true,
    state: randomState(),
  });
  const res = await AppleID.auth.signIn();
  const identityToken = res?.authorization?.id_token;
  if (!identityToken) throw new Error('Apple did not return an identity token');
  const nm = res?.user?.name;
  const fullName = nm ? [nm.firstName, nm.lastName].filter(Boolean).join(' ').trim() : undefined;
  return { identityToken, fullName: fullName || undefined };
}
